<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="alerts"
        :row-key="'id'"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 980 }"
        class="text-nowrap align-middle quantity-alert-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_product_quantity_alerts_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'quantity'">
            <span class="stock-badge" :class="getStockClass(record)">
              {{ record.quantity }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'stock_alert'">
            <span class="alert-badge">
              {{ record.stock_alert }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'gap'">
            <span class="gap-text">
              {{ getAlertGap(record) }}
            </span>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { ProductQuantityAlertReportItem } from "@/services/report";

export default defineComponent({
  name: "ProductQuantityAlert",
  props: {
    alerts: {
      type: Array as () => ProductQuantityAlertReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    i18nScope: {
      type: String,
      default: "product_quantity_alert_report_page",
    },
  },
  emits: ["table-change"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_code`), key: "sku", dataIndex: "SKU", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_product`), key: "product_name", dataIndex: "product_name", width: 240 },
      { title: t(`${props.i18nScope}.table_category`), key: "category_name", dataIndex: "category_name", width: 180 },
      { title: t(`${props.i18nScope}.table_warehouse`), key: "warehouse_name", dataIndex: "warehouse_name", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_in_stock`), key: "quantity", dataIndex: "quantity", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_alert_qty`), key: "stock_alert", dataIndex: "stock_alert", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_shortage`), key: "gap", width: 140, align: "center" as const },
    ]);

    const getAlertGap = (record: ProductQuantityAlertReportItem) => {
      const quantity = Number(record.quantity ?? 0);
      const alertQty = Number(record.stock_alert ?? 0);
      return Math.max(alertQty - quantity, 0);
    };

    const getStockClass = (record: ProductQuantityAlertReportItem) => {
      return Number(record.quantity ?? 0) <= 0 ? "stock-empty" : "stock-low";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      columns,
      getAlertGap,
      getStockClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.quantity-alert-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.quantity-alert-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.stock-badge,
.alert-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.stock-low {
  color: #d48a00;
  background: #fff4db;
}

.stock-empty {
  color: #d33c43;
  background: #fdecec;
}

.alert-badge {
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
}

.gap-text {
  font-weight: 700;
  color: #1f2940;
}
</style>
